import { useMemo } from 'react';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ParsedUniqueId, parseUniqueId } from '@/typings/parseUniqueId';
import { Prettify } from '@/util/type';
import { useAccountProjects } from '../discovery/accountProjects';
import { ResponseAppliedLineageNode } from '../discovery/appliedLineage';
import { useAppliedLineage } from './useAppliedLineage';

type Node = Prettify<
  Omit<ResponseAppliedLineageNode, 'uniqueId'> & { uniqueId: ParsedUniqueId }
>;

export const usePackage = (packageName: string | undefined) => {
  const { environmentId } = useDbtCloudProject();
  const { data, fetching, error } = useAppliedLineage({ environmentId, select: '*' });
  const hasData = !!data;

  const packageQuery = useMemo(() => {
    const lineage = data?.environment.applied.lineage;
    const packageResources = lineage?.reduce((acc, resource) => {
      const uniqueId = parseUniqueId(resource.uniqueId);
      if (uniqueId.packageName === packageName) {
        acc.push({ ...resource, uniqueId });
      }
      return acc;
    }, [] as Node[]);

    return {
      fetching,
      error,
      data: hasData
        ? {
            applied: {
              lastUpdatedAt: data.environment.applied.lastUpdatedAt,
              resources: packageResources,
              totalTables: packageResources?.length || 0,
            },
          }
        : null,
    };
  }, [
    data?.environment.applied.lineage,
    fetching,
    error,
    hasData,
    data?.environment.applied.lastUpdatedAt,
    packageName,
  ]);

  return packageQuery;
};

/** Used to determine if a resource is external to the core package, given the package name */
export const useIsExternal = (
  packageName: string | null | undefined,
  projectId?: number,
) => {
  const { accountId = 0 } = useDbtCloudAccount();
  const { projectId: inheritedProjectId } = useDbtCloudProject();
  const validProjectId = projectId ?? inheritedProjectId ?? 0;
  const [accountProjectsQuery] = useAccountProjects({ accountId });

  const coreProjectName = accountProjectsQuery.data?.account?.projects?.find(
    (x) => x.projectId === validProjectId,
  )?.coreProjectName;

  return {
    fetching: accountProjectsQuery.fetching,
    isExternal: packageName !== coreProjectName,
    coreProjectName,
  };
};
